// Product Service - Handles product and category data
import { I18nManager } from "./i18n-manager";

export interface Category {
  id: string;
  name: Record<string, string>; // {"sv": "...", "en": "..."}
  slug: string;
  image_url: string;
  sort_order: number;
}

export interface OptionValue {
  label: Record<string, string>; // {"sv": "...", "en": "..."}
  value: string; // Canonical identifier (English label)
}

export interface OptionAttribute {
  name: Record<string, string>; // {"sv": "...", "en": "..."}
  values: OptionValue[];
}

export interface Product {
  id: string;
  category_id: string;
  name: Record<string, string>; // {"sv": "...", "en": "..."}
  description: Record<string, string>; // {"sv": "...", "en": "..."}
  price_sek: number;
  images: string[];
  options: Record<string, OptionAttribute>;
  active: boolean;
  created_at: number;
}

export class ProductService {
  private categories: Category[] = [];
  private products: Product[] = [];
  private currentLanguage = "en";
  private apiBaseUrl = "/api";
  private i18nManager: I18nManager | null = null;

  constructor(i18nManager?: I18nManager) {
    // Get language from localStorage or default to 'en'
    this.currentLanguage = localStorage.getItem("language") || "en";
    
    // Subscribe to language changes if I18nManager is provided
    if (i18nManager) {
      this.i18nManager = i18nManager;
      i18nManager.onLanguageChange((language: string) => {
        this.currentLanguage = language;
        // Clear caches when language changes so data is reloaded with new sorting
        this.categoriesCache = null;
        this.productsCache.clear();
      });
    }
  }

  // Set current language (for backward compatibility)
  setLanguage(language: string): void {
    this.currentLanguage = language;
    localStorage.setItem("language", language);
  }

  // Get current language
  getLanguage(): string {
    return this.currentLanguage;
  }

  // Load categories from API with caching
  private categoriesCache: { data: Category[]; timestamp: number } | null = null;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  async loadCategories(): Promise<Category[]> {
    // Return cached data if still valid
    if (this.categoriesCache && Date.now() - this.categoriesCache.timestamp < this.CACHE_DURATION) {
      return this.categoriesCache.data;
    }

    try {
      // Get current language and include it in the request
      const lang = this.currentLanguage || localStorage.getItem("language") || "en";
      const url = `${this.apiBaseUrl}/categories?lang=${lang}`;
      
      const response = await fetch(url, {
        cache: 'default', // Use browser cache
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      this.categories = data || [];
      // Cache the result
      this.categoriesCache = {
        data: this.categories,
        timestamp: Date.now(),
      };
      return this.categories;
    } catch (error) {
      console.error("Failed to load categories:", error);
      // Return cached data if available, even if expired
      if (this.categoriesCache) {
        console.warn("Using stale categories cache due to network error");
        return this.categoriesCache.data;
      }
      throw error;
    }
  }

  // Load products from API with caching
  private productsCache: Map<string, { data: Product[]; timestamp: number }> = new Map();
  private readonly PRODUCTS_CACHE_DURATION = 2 * 60 * 1000; // 2 minutes

  async loadProducts(categorySlug?: string): Promise<Product[]> {
    // Include language in cache key so different languages have separate caches
    const lang = this.currentLanguage || localStorage.getItem("language") || "en";
    const cacheKey = `${categorySlug || 'all'}_${lang}`;
    
    // Return cached data if still valid
    const cached = this.productsCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.PRODUCTS_CACHE_DURATION) {
      return cached.data;
    }

    try {
      // Get current language and include it in the request
      const url = categorySlug
        ? `${this.apiBaseUrl}/products?category=${categorySlug}&lang=${lang}`
        : `${this.apiBaseUrl}/products?lang=${lang}`;

      const response = await fetch(url, {
        cache: 'default', // Use browser cache
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      this.products = data || [];
      
      // Cache the result
      this.productsCache.set(cacheKey, {
        data: this.products,
        timestamp: Date.now(),
      });
      
      return this.products;
    } catch (error) {
      console.error("Failed to load products:", error);
      // Return cached data if available, even if expired
      if (cached) {
        console.warn("Using stale products cache due to network error");
        return cached.data;
      }
      throw error;
    }
  }

  // Get product by ID with caching
  private productCache: Map<string, { data: Product; timestamp: number }> = new Map();
  private readonly PRODUCT_CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

  async getProduct(id: string): Promise<Product | null> {
    // Return cached data if still valid
    const cached = this.productCache.get(id);
    if (cached && Date.now() - cached.timestamp < this.PRODUCT_CACHE_DURATION) {
      return cached.data;
    }

    try {
      const response = await fetch(`${this.apiBaseUrl}/products/${id}`, {
        cache: 'default', // Use browser cache
      });
      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const product = await response.json();
      
      // Cache the result
      if (product) {
        this.productCache.set(id, {
          data: product,
          timestamp: Date.now(),
        });
      }
      
      return product;
    } catch (error) {
      console.error("Failed to get product:", error);
      // Return cached data if available, even if expired
      if (cached) {
        console.warn("Using stale product cache due to network error");
        return cached.data;
      }
      throw error;
    }
  }

  // Get product by ID (alias for ProductDialog compatibility)
  async getProductById(id: string): Promise<Product | null> {
    return this.getProduct(id);
  }

  // Get all categories
  getCategories(): Category[] {
    return [...this.categories];
  }

  // Get all products
  getProducts(): Product[] {
    return [...this.products];
  }

  // Get products by category
  getProductsByCategory(categorySlug: string): Product[] {
    const category = this.categories.find((cat) => cat.slug === categorySlug);
    if (!category) return [];

    return this.products.filter(
      (product) => product.category_id === category.id
    );
  }

  // Get category by slug
  getCategoryBySlug(slug: string): Category | null {
    return this.categories.find((cat) => cat.slug === slug) || null;
  }

  // Get localized name
  getLocalizedName(item: { name: Record<string, string> }): string {
    return (
      item.name[this.currentLanguage] ||
      item.name["en"] ||
      item.name["sv"] ||
      "Unknown"
    );
  }

  // Get localized description
  getLocalizedDescription(item: {
    description: Record<string, string>;
  }): string {
    return (
      item.description[this.currentLanguage] ||
      item.description["en"] ||
      item.description["sv"] ||
      ""
    );
  }

  // Get localized option name
  getLocalizedOptionName(option: OptionAttribute): string {
    return (
      option.name[this.currentLanguage] ||
      option.name["en"] ||
      option.name["sv"] ||
      "Unknown"
    );
  }

  // Get localized option value label
  getLocalizedOptionValue(optionValue: OptionValue): string {
    return (
      optionValue.label[this.currentLanguage] ||
      optionValue.label["en"] ||
      optionValue.label["sv"] ||
      optionValue.value ||
      "Unknown"
    );
  }

  // Get localized option value by canonical value string
  getLocalizedOptionValueByValue(
    option: OptionAttribute,
    value: string
  ): string {
    const optionValue = option.values.find((v) => v.value === value);
    if (!optionValue) return value;
    return this.getLocalizedOptionValue(optionValue);
  }

  // Format price
  formatPrice(price: number): string {
    return `${price} SEK`;
  }

  // Get product image URL
  getProductImageUrl(imagePath: string): string {
    if (imagePath.startsWith("http")) {
      return imagePath;
    }
    // If path already starts with /assets/ or /static/, return as-is
    if (imagePath.startsWith("/assets/") || imagePath.startsWith("/static/")) {
      return imagePath;
    }
    // Otherwise, assume it's a static file path
    return `/static/${imagePath}`;
  }

  // Get placeholder image
  getPlaceholderImage(): string {
    return "/assets/placeholder-product.jpg";
  }

  // Search products
  searchProducts(query: string): Product[] {
    const lowercaseQuery = query.toLowerCase();

    return this.products.filter((product) => {
      const name = this.getLocalizedName(product).toLowerCase();
      const description = this.getLocalizedDescription(product).toLowerCase();

      return (
        name.includes(lowercaseQuery) || description.includes(lowercaseQuery)
      );
    });
  }

  // Filter products by price range
  filterProductsByPrice(min: number, max: number): Product[] {
    return this.products.filter(
      (product) => product.price_sek >= min && product.price_sek <= max
    );
  }

  // Sort products
  sortProducts(
    products: Product[],
    sortBy: "name" | "price" | "created_at",
    order: "asc" | "desc" = "asc"
  ): Product[] {
    return [...products].sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case "name":
          const nameA = this.getLocalizedName(a).toLowerCase();
          const nameB = this.getLocalizedName(b).toLowerCase();
          comparison = nameA.localeCompare(nameB);
          break;

        case "price":
          comparison = a.price_sek - b.price_sek;
          break;

        case "created_at":
          comparison = a.created_at - b.created_at;
          break;
      }

      return order === "desc" ? -comparison : comparison;
    });
  }

  // Get product options
  getProductOptions(product: Product): Record<string, any> {
    return product.options || {};
  }

  // Validate product options
  validateProductOptions(
    product: Product,
    selectedOptions: Record<string, any>
  ): boolean {
    const productOptions = product.options || {};

    for (const [key, option] of Object.entries(productOptions)) {
      if (!selectedOptions[key]) {
        return false;
      }

      // Check if selected value exists in option values
      const selectedValue = selectedOptions[key];
      const validValue = option.values.some(
        (v) => v.value === selectedValue
      );
      if (!validValue) {
        return false;
      }
    }

    return true;
  }

  // Generate product cart item
  createCartItem(
    product: Product,
    quantity: number = 1,
    options: Record<string, any> = {}
  ): {
    productId: string;
    name: string;
    price: number;
    quantity: number;
    options: Record<string, any>;
    image: string;
  } {
    return {
      productId: product.id,
      name: this.getLocalizedName(product),
      price: product.price_sek,
      quantity,
      options,
      image:
        product.images.length > 0
          ? this.getProductImageUrl(product.images[0])
          : this.getPlaceholderImage(),
    };
  }

  // Cache management
  clearCache(): void {
    this.categories = [];
    this.products = [];
  }

  // Check if data is loaded
  isDataLoaded(): boolean {
    return this.categories.length > 0 && this.products.length > 0;
  }
}
